import React from "react";
import { Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

/**
 * @interface Status
 * Represents the structure of a status object.
 */
interface Status {
  error?: string;
  notMarked?: boolean;
  correct?: string;
  wrong?: string;
  not_marked?: boolean;
  isWrong?: boolean | null;
  status?: {
    undefinedByReviewer?: boolean;
    correct?: boolean;
    incorrect?: boolean;
  };
}

/**
 * @interface StatusBadgeProps
 * Props for the StatusBadge component.
 */
interface StatusBadgeProps {
  status: Status | string;
}

const useStyles = makeStyles({
  correta: {
    backgroundColor: "#28a745",
    color: "#ffffff",
  },
  nao_marcada: {
    backgroundColor: "#fff3cd",
    color: "#856404",
  },
  incorreta: {
    backgroundColor: "#dc3545",
    color: "#ffffff",
  },
  erro: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
  },
  desconhecido: {
    backgroundColor: "#e2e3e5",
    color: "#383d41",
  },
});

/**
 * Retorna a mensagem de status legível.
 */
const getStatusMessage = (response: any) => {
  if (response.isWrong === null) return "Não Corrigida";
  if (response.isWrong === true) return "Incorreta";
  if (response.isWrong === false) return "Correta";

  if (response.status) {
    if (response.status.undefinedByReviewer) return "Não Corrigida";
    if (response.status.correct) return "Correta";
    if (response.status.incorrect) return "Incorreta";
  }

  return "Desconhecido";
};

/**
 * Exibe um badge representando o status da resposta.
 */
export function StatusBadge({ status }: StatusBadgeProps) {
  const classes = useStyles();

  // Converte string JSON para objeto quando necessário
  let parsedStatus: Status | null = null;
  if (typeof status === "object") {
    parsedStatus = status as Status;
  } else if (typeof status === "string") {
    try {
      parsedStatus = JSON.parse(status);
    } catch {
      parsedStatus = null;
    }
  }

  let badgeClass = classes.desconhecido;
  let label = "Desconhecido";

  if (parsedStatus) {
    const notMarked = parsedStatus.notMarked ?? parsedStatus.not_marked;

    if (parsedStatus.error) {
      badgeClass = classes.erro;
      label = `Erro: ${parsedStatus.error}`;
    } else if (notMarked) {
      badgeClass = classes.nao_marcada;
      label = "Não Marcada";
    } else if (parsedStatus.wrong) {
      badgeClass = classes.incorreta;
      label = parsedStatus.wrong;
    } else if (parsedStatus.correct) {
      badgeClass = classes.correta;
      label = parsedStatus.correct;
    } else {
      const statusMessage = getStatusMessage(parsedStatus);
      label = statusMessage;

      if (statusMessage === "Não Corrigida") {
        badgeClass = classes.nao_marcada;
      } else if (statusMessage === "Incorreta") {
        badgeClass = classes.incorreta;
      } else if (statusMessage === "Correta") {
        badgeClass = classes.correta;
      }
    }
  } else if (typeof status === "string") {
    const statusLower = status.toLowerCase();

    if (statusLower.includes("erro")) {
      badgeClass = classes.erro;
      label = status;
    } else if (
      statusLower === "não marcada" ||
      statusLower === "nao marcada" ||
      statusLower === "não corrigida" ||
      statusLower === "nao corrigida"
    ) {
      badgeClass = classes.nao_marcada;
      label = statusLower.includes("corrigida")
        ? "Não Corrigida"
        : "Não Marcada";
    } else if (statusLower === "incorreta") {
      // <— prioridade para “incorreta”
      badgeClass = classes.incorreta;
      label = "Incorreta";
    } else if (statusLower.includes("mas correta")) {
      badgeClass = classes.incorreta;
      label = status;
    } else if (statusLower.includes("correta")) {
      badgeClass = classes.correta;
      label = status;
    }
  }

  return <Chip label={label} className={badgeClass} />;
}
